import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Chip,
    TextField,
    Typography,
    Checkbox,
    Paper,
    CircularProgress,
    ListItemText,
    Tooltip,
    IconButton,
    InputAdornment,
    Autocomplete,
} from "@mui/material";
import { useGetTenanciesQuery, useLazyGetTenanciesQuery } from "../store/services/tenancy.service";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import HelpIcon from "@mui/icons-material/HelpOutline";

// Configuration constants
const SEARCH_DEBOUNCE_MS = 300;
const INITIAL_PAGE_SIZE = 500;
const SEARCH_PAGE_SIZE = 100;

// Global option (ID 0) for sharing with all tenancies
const GLOBAL_OPTION = { tenancyId: 0, name: "All Tenancies" };

function TenancySelectionComponent({ selectedTenancies, onChange, disabled }) {
    const [inputValue, setInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [displayedOptions, setDisplayedOptions] = useState([]);

    // Cache for tenancy data (to resolve names for selected IDs)
    const [tenancyCache, setTenancyCache] = useState({});

    // Debounce timer ref
    const debounceTimerRef = useRef(null);

    // Initial load of tenancies
    const { data: initialTenanciesData = [], isLoading: initialLoading } = useGetTenanciesQuery({
        perPage: INITIAL_PAGE_SIZE,
        page: 0,
        includes: "",
        searchTerm: "",
        additionalQueryParams: ""
    });

    // Lazy query for server-side search
    const [
        triggerSearch,
        { data: searchResults = [], isLoading: searchLoading, isFetching: searchFetching }
    ] = useLazyGetTenanciesQuery();

    // Build and update tenancy cache when initial data loads
    useEffect(() => {
        if (initialTenanciesData.length > 0) {
            setTenancyCache(prev => {
                const newCache = { ...prev };
                initialTenanciesData.forEach(t => {
                    newCache[t.tenancyId] = t;
                });
                return newCache;
            });
            // Set initial displayed options (global option + initial data)
            setDisplayedOptions([GLOBAL_OPTION, ...initialTenanciesData]);
        }
    }, [initialTenanciesData]);

    // Update cache and displayed options when search results arrive
    useEffect(() => {
        if (searchQuery && !searchLoading && !searchFetching) {
            if (searchResults.length > 0) {
                // Update cache with search results
                setTenancyCache(prev => {
                    const newCache = { ...prev };
                    searchResults.forEach(t => {
                        newCache[t.tenancyId] = t;
                    });
                    return newCache;
                });
                // Display search results (always include global option)
                setDisplayedOptions([GLOBAL_OPTION, ...searchResults]);
            } else {
                // No results found - just show global option
                setDisplayedOptions([GLOBAL_OPTION]);
            }
            setIsSearching(false);
        }
    }, [searchResults, searchQuery, searchLoading, searchFetching]);

    // Reset to initial data when search is cleared
    useEffect(() => {
        if (!searchQuery && initialTenanciesData.length > 0) {
            setDisplayedOptions([GLOBAL_OPTION, ...initialTenanciesData]);
        }
    }, [searchQuery, initialTenanciesData]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Debounced search function
    const debouncedSearch = useCallback((query) => {
        // Clear any existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (!query || query.trim() === '') {
            // No search query - reset to initial data
            setSearchQuery('');
            setIsSearching(false);
            if (initialTenanciesData.length > 0) {
                setDisplayedOptions([GLOBAL_OPTION, ...initialTenanciesData]);
            }
            return;
        }

        setIsSearching(true);

        // Set new debounce timer
        debounceTimerRef.current = setTimeout(() => {
            setSearchQuery(query);
            triggerSearch({
                perPage: SEARCH_PAGE_SIZE,
                page: 0,
                includes: "",
                searchTerm: query.trim(),
                additionalQueryParams: ""
            });
        }, SEARCH_DEBOUNCE_MS);
    }, [initialTenanciesData, triggerSearch]);

    // Convert selectedTenancies array of IDs to array of tenancy objects
    const selectedTenancyObjects = useMemo(() => {
        if (!selectedTenancies.length) return [];

        return selectedTenancies.map(id => {
            // Check cache first
            if (tenancyCache[id]) {
                return tenancyCache[id];
            }
            // Check if it's the global option
            if (id === 0) {
                return GLOBAL_OPTION;
            }
            // Fallback for unknown tenancies
            return { tenancyId: id, name: `Tenancy ${id}` };
        });
    }, [selectedTenancies, tenancyCache]);

    // Ensure selected tenancies are always in the options list
    const optionsWithSelected = useMemo(() => {
        const displayedIds = new Set(displayedOptions.map(o => o.tenancyId));
        const missingSelected = selectedTenancyObjects.filter(
            t => !displayedIds.has(t.tenancyId)
        );

        if (missingSelected.length > 0) {
            // Insert missing selected items after the global option
            const [globalOpt, ...rest] = displayedOptions;
            return [globalOpt, ...missingSelected, ...rest];
        }

        return displayedOptions;
    }, [displayedOptions, selectedTenancyObjects]);

    const handleDelete = (tenancyIdToDelete, event) => {
        // Stop event propagation to prevent the dropdown from opening
        if (event) {
            event.stopPropagation();
        }

        // Remove the selected tenancy from the list
        const newSelectedTenancies = selectedTenancies.filter(id => id !== tenancyIdToDelete);
        onChange(newSelectedTenancies);
    };

    const handleChange = (event, newValues) => {
        // Extract just the IDs from the tenancy objects
        const newValueIds = newValues.map(item => item.tenancyId);

        // If "All Tenancies" (ID 0) is selected, just use that
        if (newValueIds.includes(0)) {
            onChange([0]);
        } else {
            onChange(newValueIds);
        }
    };

    const handleInputChange = (event, newInputValue, reason) => {
        setInputValue(newInputValue);

        // Only trigger search on user input, not on selection or reset
        if (reason === 'input') {
            debouncedSearch(newInputValue);
        }
    };

    // Client-side filtering for immediate feedback (before server response)
    const filterOptions = useCallback((options, { inputValue: filterInput }) => {
        // If we have an active server-side search, don't filter client-side
        if (searchQuery) {
            return options;
        }

        // Client-side filter for initial data
        if (!filterInput) return options;

        const searchText = filterInput.toLowerCase();
        return options.filter(
            option =>
                option.name.toLowerCase().includes(searchText) ||
                option.tenancyId.toString().includes(searchText)
        );
    }, [searchQuery]);

    const isLoading = initialLoading || isSearching || searchFetching;

    // No options text
    const noOptionsText = useMemo(() => {
        if (isSearching || searchFetching) {
            return 'Searching...';
        }
        if (searchQuery && displayedOptions.length <= 1) { // Only global option
            return `No tenancies found matching "${searchQuery}"`;
        }
        return 'No tenancies found';
    }, [isSearching, searchFetching, searchQuery, displayedOptions.length]);

    if (initialLoading) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 2 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography>Loading tenancies...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Share with Tenancies
                </Typography>
                <Tooltip title="Select tenancies to share this course with. Selecting 'All Tenancies' will share with everyone. Type to search all tenancies by ID or name." placement="top">
                    <IconButton size="small" sx={{ ml: 1 }}>
                        <HelpIcon sx={{ color: "secondary.main" }} />
                    </IconButton>
                </Tooltip>
            </Box>

            <Autocomplete
                multiple
                id="tenancy-selection"
                options={optionsWithSelected}
                value={selectedTenancyObjects}
                onChange={handleChange}
                getOptionLabel={(option) =>
                    !option ? '' :
                    option.tenancyId === 0 ?
                        option.name :
                        `[${option.tenancyId}] ${option.name}`
                }
                isOptionEqualToValue={(option, value) => option?.tenancyId === value?.tenancyId}
                filterOptions={filterOptions}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                disabled={disabled}
                loading={isLoading}
                noOptionsText={noOptionsText}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={selectedTenancies.length ? "" : "Search tenancies by ID or name"}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    <InputAdornment position="start">
                                        {isLoading ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <SearchIcon />
                                        )}
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                </>
                            )
                        }}
                    />
                )}
                renderOption={(props, option, { selected }) => {
                    // Disable selection if "All Tenancies" is already selected
                    const isDisabled = selectedTenancies.includes(0) && option.tenancyId !== 0;

                    return (
                        <li
                            {...props}
                            style={{
                                opacity: isDisabled ? 0.5 : 1,
                                pointerEvents: isDisabled ? 'none' : 'auto'
                            }}
                        >
                            <Checkbox
                                checked={selected}
                                sx={{ mr: 1 }}
                            />
                            <ListItemText
                                primary={
                                    <Box component="span" sx={{ fontWeight: option.tenancyId === 0 ? 'bold' : 'normal' }}>
                                        {option.tenancyId === 0 ? (
                                            option.name
                                        ) : (
                                            <React.Fragment>
                                                <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                                                    [{option.tenancyId}]
                                                </Box>
                                                {option.name}
                                            </React.Fragment>
                                        )}
                                    </Box>
                                }
                            />
                        </li>
                    );
                }}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            key={option.tenancyId}
                            label={option.tenancyId === 0 ?
                                option.name :
                                `[${option.tenancyId}] ${option.name}`
                            }
                            color={option.tenancyId === 0 ? "primary" : "default"}
                            deleteIcon={
                                <CancelIcon
                                    onMouseDown={(event) => event.stopPropagation()}
                                />
                            }
                            onDelete={(event) => handleDelete(option.tenancyId, event)}
                        />
                    ))
                }
                ListboxProps={{
                    style: { maxHeight: 300 }
                }}
                PaperComponent={({ children, ...props }) => (
                    <Paper {...props} elevation={8}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            p: 1,
                            bgcolor: 'grey.100',
                            borderBottom: 1,
                            borderColor: 'divider'
                        }}>
                            <Typography variant="caption" color="text.secondary">
                                {searchQuery
                                    ? `Search results: ${displayedOptions.length - 1}`
                                    : `Showing: ${displayedOptions.length - 1} tenancies`
                                }
                            </Typography>
                            {(isSearching || searchFetching) && (
                                <CircularProgress size={12} />
                            )}
                        </Box>
                        {children}
                    </Paper>
                )}
            />
        </Box>
    );
}

TenancySelectionComponent.propTypes = {
    selectedTenancies: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool
};

TenancySelectionComponent.defaultProps = {
    selectedTenancies: [],
    disabled: false
};

export default TenancySelectionComponent;